import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Brand from "@/components/Brand";
import SiteHead from "@/components/SiteHead";
import api, { mediaUrl } from "@/lib/api";
import { ArrowRight } from "lucide-react";

function fmt(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
}

export default function BlogIndex() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/blog/posts").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, []);

  return (
    <div className="lp-paper text-stone-900 min-h-screen font-sans antialiased">
      <SiteHead
        title="Blog — Mera Startup | How to sell online in India"
        description="Practical guides for Indian shop owners: launch an ecommerce website, take COD and UPI, and grow on Google — from the Mera Startup team."
        path="/blog"
      />
      <header className="sticky top-0 z-50 border-b border-stone-900/10 bg-[#FBF7F0]/85 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="blog-brand"><Brand variant="light" size="xl" /></Link>
          <nav className="flex items-center gap-5 text-sm font-medium text-stone-600">
            <Link to="/blog" className="text-stone-900 font-semibold" data-testid="blog-nav">Blog</Link>
            <Link to="/pricing" className="hover:text-stone-900 hidden sm:inline">Pricing</Link>
            <Link to="/signup?plan=website" className="rounded-full bg-stone-900 text-white px-4 py-2 font-semibold border-2 border-stone-900">Get Started</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-12 sm:py-16">
        <p className="text-xs font-bold uppercase tracking-wider text-orange-600">Mera Startup Journal</p>
        <h1 className="mt-2 font-display text-3xl sm:text-5xl font-extrabold tracking-tight">Guides to sell online in India</h1>
        <p className="mt-4 text-lg text-stone-600 max-w-2xl">How to launch a store, take payments, and get found on Google — written for kirana, boutique and small-brand owners.</p>

        {!items ? (
          <p className="mt-12 text-stone-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="mt-12 text-stone-500" data-testid="blog-empty">New articles are on the way. Meanwhile, <Link to="/signup" className="text-orange-700 font-semibold underline">start your store</Link>.</p>
        ) : (
          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            {items.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group rounded-2xl bg-white border-2 border-stone-900 lp-hard-sm overflow-hidden hover:-translate-y-0.5 transition-transform">
                {p.cover_image ? (
                  <img src={mediaUrl(p.cover_image)} alt={p.cover_alt || p.title} className="w-full h-44 object-cover" loading="lazy" />
                ) : (
                  <div className="h-28 bg-gradient-to-br from-orange-100 to-stone-100" />
                )}
                <div className="p-5">
                  <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">{p.category} · {fmt(p.published_at)} · {p.reading_minutes} min</div>
                  <h2 className="mt-2 font-display text-xl font-extrabold group-hover:text-orange-700">{p.title}</h2>
                  <p className="mt-2 text-sm text-stone-600 line-clamp-3">{p.excerpt}</p>
                  <span className="mt-3 inline-flex items-center text-sm font-semibold text-orange-700">Read <ArrowRight className="w-4 h-4 ml-1" /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
